import { useState, type ReactNode } from 'react';
import { Transition } from '@headlessui/react';
import cn from '../utils/cn';
import { fadeTransition, overlayBackdrop } from './overlay';
import KoobLoader from './KoobLoader';

type KoobLoadingOverlayProps = {
  title?: ReactNode;
  description?: ReactNode;
  /** full: an opaque page (app start); semi: the page shows through, blurred;
   * none: clear, the page stays visible but blocked. */
  opacity?: 'full' | 'semi' | 'none';
  /** Shown instead of Koob's loader (white-labelled screens). */
  logoUrl?: string;
  logoAlt?: string;
  /** No brand at all: neither Koob's loader nor a custom logo. */
  hideLogo?: boolean;
};

/** Blocks the page while a long job runs (export, trip generation), saying
 * what happens; also the app's start screen. */
export default function KoobLoadingOverlay({
  title,
  description,
  opacity = 'full',
  logoUrl,
  logoAlt,
  hideLogo = false,
}: KoobLoadingOverlayProps) {
  const [logoFailed, setLogoFailed] = useState(false);

  // Koob's own screens show its animated B; a white-labelled one shows its
  // logo over a plain ring, and a logo that fails to load falls back to the
  // ring alone, never to Koob's brand.
  const koob = !hideLogo && !logoUrl;
  const showCustomLogo = !hideLogo && !!logoUrl && !logoFailed;

  return (
    // Fades in like every overlay; callers unmount it, so it leaves at once.
    <Transition
      show
      appear
      {...fadeTransition}
      role="status"
      aria-live="polite"
      className={cn(
        // !m-0: a parent's space-y-* would push it down by its gap.
        'fixed inset-0 !m-0 z-fullscreen flex items-center justify-center p-6',
        {
          full: 'bg-navy-50',
          semi: overlayBackdrop,
          none: 'bg-transparent',
        }[opacity],
      )}
    >
      {/* Over the page, the content sits on a card, as a modal does. */}
      <div
        className={cn(
          'flex max-w-md flex-col items-center gap-4 text-center',
          opacity !== 'full' &&
            'rounded-2xl bg-white px-10 py-8 shadow-[0_24px_60px_rgba(15,26,46,.35)]',
        )}
      >
        {showCustomLogo && (
          <img
            src={logoUrl}
            alt={logoAlt ?? ''}
            onError={() => setLogoFailed(true)}
            className="h-20 w-auto max-w-[240px] object-contain"
          />
        )}
        {koob ? (
          <KoobLoader size={88} />
        ) : (
          // White-labelled: a plain ring, no Koob mark.
          <span className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-gray-200 border-t-gray-500" />
        )}
        {title && (
          <h2 className="m-0 text-[22px] font-semibold leading-[30px] text-k-h">
            {title}
          </h2>
        )}
        {description && (
          <p className="m-0 text-[15px] leading-6 text-k-body">{description}</p>
        )}
      </div>
    </Transition>
  );
}
